/**
 * Cognito Configuration
 *
 * Configuration for AWS Cognito authentication using Hosted UI
 *
 * Redirect and logout URIs are derived from the current browser origin at runtime
 * (`window.location.origin`) so the same build works behind a custom domain, CloudFront,
 * or localhost. Each origin + path must be registered in the Cognito app client
 * (callback URLs and sign-out URLs).
 */
import { UserManager, WebStorageStateStore } from "oidc-client-ts";

/** `/auth/callback` on the current origin, or build-time env when `window` is unavailable. */
export function resolveOAuthRedirectUri(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/auth/callback`;
  }
  return import.meta.env.VITE_COGNITO_REDIRECT_URI;
}

/** Root URL on the current origin (Hosted UI sign-out), or build-time env as fallback. */
export function resolveOAuthLogoutUri(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return import.meta.env.VITE_COGNITO_LOGOUT_URI;
}

export const userManager: UserManager = new UserManager({
  authority: `https://cognito-idp.${import.meta.env.VITE_COGNITO_REGION}.amazonaws.com/${import.meta.env.VITE_COGNITO_USER_POOL_ID}`,
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  redirect_uri: resolveOAuthRedirectUri(),
  response_type: "code",
  scope: "aws.cognito.signin.user.admin email openid profile",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
});

/**
 * Get the logout URL for Cognito Hosted UI
 */
export function getLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    logout_uri: resolveOAuthLogoutUri(),
  });

  return `${import.meta.env.VITE_COGNITO_DOMAIN}/logout?${params.toString()}`;
}
